import { isEqual, last, template as compile } from 'lodash-es';
import SEO_TEMPLATES_DEFAULT from './seo-templates-default.json';
import SEO_TEMPLATES_SMARTPHONES from './seo-templates-smartphones.json';
import SEO_TEMPLATES_SMARTPHONES_UND_ZUBEHOER from './seo-templates-smartphones-und-zubehoer.json';
import SEO_TEMPLATES_TABLETS from './seo-templates-tablets.json';
import SEO_TEMPLATES_TABLETS_UND_ZUBEHOER from './seo-templates-tablets-zubehoer.json';
import SEO_TEMPLATES_ACCESSORIES from './seo-templates-accessories.json';
import SEO_TEMPLATES_TV from './seo-templates-tv.json';
import SEO_TEMPLATES_SMARTHOME from './seo-templates-smart-home.json';
import type {
  ListQuery,
  ListQueryVariables,
  OfferProductCategoryAggregation,
} from '~/lib/goliath/goliath.types';
import traverseAggregations from '~/lib/aggregation-utils/traverse';

type TemplateName = 'title' | 'description' | 'pageHeadline';

type ProductCategoryAggregations = NonNullable<
  NonNullable<NonNullable<ListQuery['offers']>['aggregations']>['productCategories']
>;

type ProductCategoryAggregationEntry = NonNullable<
  NonNullable<ProductCategoryAggregations[number]>['productCategory']
>;

type MappedFilters = {
  selectedBrands: NonNullable<
    NonNullable<NonNullable<NonNullable<ListQuery['offers']>['aggregations']>['brands']>[number]
  >['brand'][];
  selectedCompatibleProductFamilyNames: string[];
  selectedColorNames: string[];
  selectedProductCategories: ProductCategoryAggregationEntry[];
  selectedCategoryNames: string[];
  mainCategory: string | undefined;
  ancestorProductCategories: ProductCategoryAggregationEntry[];
};

type SeoTemplate = {
  selectedFilters?: string[];
  pageHeadline?: string;
  title?: string;
  description?: string;
};

export const getMappedCategories = (
  variables: ListQueryVariables,
  offers: ListQuery['offers']
): MappedFilters => {
  const filter = variables.filter || {};
  const aggregations = offers?.aggregations;

  const getParentProductCategoryPath = () => {
    if (!filter?.productCategoryPaths) return undefined;
    const segments = filter.productCategoryPaths[0].split('/');
    return segments.slice(0, segments.length - 1).join('/') || undefined;
  };

  const parentProductCategoryPath = getParentProductCategoryPath();

  const getAncestorProductCategoryAggregations = () => {
    if (!parentProductCategoryPath || !aggregations?.productCategories) return [];

    return traverseAggregations(aggregations.productCategories, {
      filter: (agg: OfferProductCategoryAggregation | undefined) => agg?.selected,
      continue: (agg: OfferProductCategoryAggregation | undefined) =>
        agg?.selected && agg?.productCategory?.path !== parentProductCategoryPath,
    }) as ProductCategoryAggregations;
  };

  const ancestorProductCategoryAggregations = getAncestorProductCategoryAggregations();

  const ancestorProductCategories = ancestorProductCategoryAggregations
    .map((aggregation) => aggregation?.productCategory)
    .filter(Boolean);

  const selectedColors = (aggregations?.colors || [])
    .filter((agg) => agg?.selected)
    .map((agg) => agg?.color)
    .filter(Boolean);

  const selectedCompatibleProductFamilies = (aggregations?.compatibleProductFamilies || [])
    .filter((agg) => agg?.selected)
    .map((agg) => agg?.productFamily)
    .filter(Boolean);

  const selectedBrands = (aggregations?.brands || [])
    .filter((agg) => agg?.selected)
    .map((agg) => agg?.brand)
    .filter(Boolean);

  const getSelectedProductCategories = () => {
    const parentProductCategoryAggregation = last(ancestorProductCategoryAggregations);

    const getSiblingProductCategoryAggregations = () => {
      let aggs;
      if (parentProductCategoryAggregation) {
        aggs = parentProductCategoryAggregation.children;
      } else {
        aggs = aggregations?.productCategories || [];
      }
      return aggs || [];
    };

    const specificNames = [{ path: 'smart-home/bundles', name: 'Smart Home Bundles' }];
    return getSiblingProductCategoryAggregations()
      .filter((agg) => agg?.selected)
      .map((agg) => ({
        ...agg?.productCategory,
        name:
          specificNames.find((item) => item.path === agg?.productCategory?.path)?.name ||
          agg?.productCategory?.name,
      })) as ProductCategoryAggregationEntry[];
  };

  const selectedProductCategories = getSelectedProductCategories();

  return {
    mainCategory: filter.productCategoryPaths?.[0]?.split('/')?.[0],
    ancestorProductCategories,
    selectedColorNames: selectedColors.map((color) => color.name).filter(Boolean),
    selectedCompatibleProductFamilyNames: selectedCompatibleProductFamilies
      .map((family) => family.model)
      .filter(Boolean),
    selectedBrands,
    selectedProductCategories,
    selectedCategoryNames: selectedProductCategories
      ?.map((category) => category.name)
      .filter(Boolean),
  };
};

const getCategoryTemplate = (mappedFilters: MappedFilters) => {
  const paths = mappedFilters.selectedProductCategories.map(({ path }) => path);
  if (paths.includes('smartphones-und-handys/smartphones')) {
    return SEO_TEMPLATES_SMARTPHONES;
  }

  if (paths.includes('tablets-und-zubehoer/tablets')) {
    return SEO_TEMPLATES_TABLETS;
  }

  const categoryTemplates: Record<string, SeoTemplate[]> = {
    wearables: SEO_TEMPLATES_ACCESSORIES,
    tv: SEO_TEMPLATES_TV,
    'tablets-und-zubehoer': SEO_TEMPLATES_TABLETS_UND_ZUBEHOER,
    zubehoer: SEO_TEMPLATES_ACCESSORIES,
    'smart-home': SEO_TEMPLATES_SMARTHOME,
    'smartphones-und-handys': SEO_TEMPLATES_SMARTPHONES_UND_ZUBEHOER,
  };

  return categoryTemplates[mappedFilters.mainCategory || ''] || SEO_TEMPLATES_DEFAULT;
};

const getSeoTemplate = (mappedFilters: MappedFilters) => {
  const {
    selectedBrands,
    selectedCompatibleProductFamilyNames,
    selectedColorNames,
    mainCategory,
    selectedProductCategories,
    selectedCategoryNames,
  } = mappedFilters;

  const selectedBrandNames = selectedBrands.map((brand) => brand?.name).filter(Boolean);

  const isBrandSelected = selectedBrandNames.length > 0 && selectedCategoryNames.length < 2;

  const isColorSelected =
    selectedColorNames.length > 0 &&
    selectedBrandNames.length < 2 &&
    selectedCategoryNames.length < 2;

  let categoryType;
  if (mainCategory === 'smart-home') {
    categoryType =
      selectedProductCategories.length === 1 && selectedProductCategories[0].path === mainCategory
        ? 'mainCategory'
        : 'category';
  } else {
    categoryType = selectedCategoryNames.length > 0 ? 'category' : null;
  }

  const selected = [
    isBrandSelected ? 'brand' : null,
    isColorSelected ? 'color' : null,
    categoryType,
    selectedCompatibleProductFamilyNames.length > 0 ? 'compatibility' : null,
  ]
    .filter(Boolean)
    .sort();

  const template = getCategoryTemplate(mappedFilters);

  const selectedTemplate = template.find((t) => isEqual(t.selectedFilters?.sort(), selected));
  return (
    selectedTemplate || {
      pageHeadline: 'Geräte und Zubehör',
      title: 'Smartphones, Tablets und Zubehör',
      description:
        'Große Auswahl an Handys & Smartphones, Tablets und Zubehör. Geprüfte Sicherheit ✓ Schnelle Lieferzeiten ✓ Jetzt entdecken und bestellen!',
    }
  );
};

export const getSeoTemplateContent = (
  variables: ListQueryVariables,
  offers: ListQuery['offers']
) => {
  const mappedFilters = getMappedCategories(variables, offers);

  const template = getSeoTemplate(mappedFilters);

  const {
    selectedBrands,
    selectedCompatibleProductFamilyNames,
    selectedColorNames,
    selectedCategoryNames,
  } = mappedFilters;

  const selectedBrandNames = selectedBrands.map((brand) => brand?.name).filter(Boolean);

  const seoTextBySelectedAggregations = (templateName: TemplateName) => {
    const createdFilteredName = (nameArr: string[]) => {
      switch (nameArr.length) {
        case 0:
          return '';
        case 1:
          return nameArr[0];
        case 2:
          return nameArr.join(' oder ');
        default:
          return `${nameArr[0]}, ${nameArr[1]}…`;
      }
    };

    const brand = createdFilteredName(selectedBrandNames);
    let category = createdFilteredName(selectedCategoryNames);
    const mainCategoryFiltered = createdFilteredName(selectedCategoryNames);
    const color = createdFilteredName(selectedColorNames);
    const compatibility = selectedCompatibleProductFamilyNames.join(' oder ');

    if (category === 'Smartphones & Handys') {
      category = 'Smartphones und Zubehör';
    }
    if (category === 'Tablets & Zubehör') {
      category = 'Tablets und Zubehör';
    }
    if (category === 'Sonstiges') {
      category = 'Sonstiges Zubehör ';
    }

    return compile(template[templateName] || '')({
      brand,
      category,
      mainCategory: mainCategoryFiltered,
      color,
      compatibility,
    });
  };

  return {
    title: seoTextBySelectedAggregations('title'),
    pageHeadline: seoTextBySelectedAggregations('pageHeadline'),
    description: seoTextBySelectedAggregations('description'),
  };
};
